/**
 * Custom Progress Bar for YouTube™ — restyles the YouTube seek bar
 *
 * Declares a single web-accessible-resource entry matched to every http(s) origin with
 * no `use_dynamic_url`, exposing `assets/*.*`, `libs/*.*`, `style.js` and
 * `custom-progress-bar.js`. The bundled icon set and scripts are reachable from any
 * page at a fixed URL — despite the extension only doing anything on YouTube.
 *
 * The 128px icon is the cleanest signal: it lives under the exposed `assets/` group and
 * loads through `probeImage` with no CORS grant, so it survives a strict page CSP.
 *
 * Manifest verified against version 3.8.16.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'nbkomboflhdlliegkaiepilnfmophgfg';

export const customProgressBar: ExtensionDefinition = {
  id: ID,
  name: 'Custom Progress Bar for YouTube™',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'assets, libs and named scripts exposed to every http(s) origin with no dynamic URL. ' +
    'The icon loads without a CORS grant, so detection is conclusive from any page.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icons/icon128.png via <img> — the toolbar icon, exposed to every ' +
        'http(s) origin and readable without a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'assets/icons/icon128.png'),
    },
    {
      id: 'war-icon-256',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icons/icon256.png via <img> — a second packaged icon corroborating ' +
        'the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/icons/icon256.png'),
    },
    {
      id: 'war-style-js',
      kind: 'web-accessible-resource',
      description: 'Fetch style.js — the seek-bar styling script, an independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'style.js'),
    },
  ],
};
